// Face enrollment card shown under the Check in / Check out buttons on the
// attendance page: consent + enrollment state and the Enroll action. Employees
// enroll ONCE — afterwards only HR can reset the enrollment (attendance feature
// "face_reset"); the one exception is a face-model upgrade, when the server
// reports the stored templates as outdated and lets the employee re-enroll.
// Renders nothing while the company has face check-in OFF or the client's
// biometric switch is off.
import React, { useEffect, useRef, useState } from "react";
import { ScanFace, CheckCircle2, XCircle, AlertTriangle, Lock } from "lucide-react";
import { useFaceStatus } from "./useFaceStatus";
import FaceConsentModal from "./FaceConsentModal";
import FaceEnrolModal from "./FaceEnrolModal";
import { faceText, fill } from "./faceText";

export default function FaceEnrolSection({ face: faceProp = null, isDarkMode = false, language = "en", isRTL = false, startSignal = 0 }) {
  // The attendance page shares its status hook; standalone use loads its own.
  const own = useFaceStatus({ enabled: !faceProp });
  const face = faceProp || own;
  const t = faceText(language);
  const [consentOpen, setConsentOpen] = useState(false);
  const [enrolOpen, setEnrolOpen] = useState(false);
  const [notice, setNotice] = useState(null); // { kind: "success" | "error", text }
  const lastSignal = useRef(0);

  const status = face.status || {};
  const canEnrol = status.canEnrol ?? (!status.enrolled || status.modelVersionOk === false);
  const locked = Boolean(status.enrolled) && !canEnrol;

  const startEnrol = () => {
    if (locked) return;
    setNotice(null);
    if (!status.consented) setConsentOpen(true);
    else setEnrolOpen(true);
  };

  // The page's set-up hint, the REQUIRED prompt and a 409 from a check-in open the flow here.
  useEffect(() => {
    if (!startSignal || startSignal === lastSignal.current || face.loading) return;
    lastSignal.current = startSignal;
    startEnrol();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startSignal, face.loading]);

  if (face.loading || face.isOff || !status.available) return null;

  const text = isDarkMode ? "rgba(255,255,255,0.88)" : "#0f172a";
  const muted = isDarkMode ? "rgba(255,255,255,0.62)" : "rgba(15,23,42,0.65)";
  const border = isDarkMode ? "rgba(255,255,255,0.1)" : "rgba(15,23,42,0.1)";
  const needsReenrol = Boolean(status.enrolled) && status.modelVersionOk === false;
  const stateColor = needsReenrol ? "#d97706" : status.enrolled ? "#22c55e" : "#ef4444";
  const StateIcon = needsReenrol ? AlertTriangle : status.enrolled ? CheckCircle2 : XCircle;
  const stateLabel = needsReenrol ? t.modelOutdated : status.enrolled ? t.enrolled : t.notEnrolled;
  const enrolledOn = status.enrolled && status.enrolledAt
    ? new Date(status.enrolledAt).toLocaleDateString(language === "ar" ? "ar-EG" : "en-GB")
    : null;

  const modeWord = face.isRequired ? t.modeRequired : t.modeOptional;
  // One secondary line only: the fresh enroll notice, else the HR-reset note
  // (enrolled) or the one-line reason to enroll (not enrolled).
  const secondary = notice
    ? { text: notice.text, color: notice.kind === "success" ? "#22c55e" : "#ef4444", bold: true }
    : locked
      ? { text: t.enrolledLocked, color: muted, Icon: Lock, testId: "face-enrol-locked" }
      : !status.enrolled
        ? { text: face.isRequired ? t.enrolRequiredHint : t.enrolOptionalHint, color: muted }
        : null;

  return (
    <div
      data-testid="face-enrol-section"
      dir={isRTL ? "rtl" : "ltr"}
      style={{
        margin: "0 auto 28px", maxWidth: 680, padding: "14px 16px", borderRadius: 14,
        border: `1px solid ${border}`, background: isDarkMode ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.03)",
        display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12, justifyContent: "space-between",
        textAlign: isRTL ? "right" : "left",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0, flex: "1 1 260px" }}>
        <div style={{ width: 42, height: 42, borderRadius: 12, display: "grid", placeItems: "center", background: `${stateColor}1f`, color: stateColor, flexShrink: 0 }}>
          <ScanFace size={22} />
        </div>
        <div style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 3 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontWeight: 800, fontSize: 14, color: text, fontFamily: "'Cairo', sans-serif" }}>{t.title}</span>
            <span
              title={fill(t.modeLabel, { mode: modeWord })}
              data-testid="face-enrol-mode"
              style={{
                fontSize: 11, fontWeight: 700, letterSpacing: "0.04em", textTransform: "capitalize", lineHeight: 1,
                padding: "4px 8px", borderRadius: 999,
                color: face.isRequired ? "#f59e0b" : muted,
                background: face.isRequired ? "rgba(245,158,11,0.14)" : (isDarkMode ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.07)"),
              }}
            >
              {modeWord}
            </span>
          </div>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 6, color: stateColor, fontSize: 13, fontWeight: 700 }} data-testid="face-enrol-state">
            <StateIcon size={14} /> {stateLabel}
            {enrolledOn && <span style={{ color: muted, fontWeight: 500 }}>· {enrolledOn}</span>}
          </div>
          {secondary && (
            <div
              role={notice ? "status" : undefined}
              data-testid={secondary.testId}
              style={{ fontSize: 12.5, lineHeight: 1.45, color: secondary.color, fontWeight: secondary.bold ? 700 : 500, display: "flex", alignItems: "center", gap: 5 }}
            >
              {secondary.Icon && <secondary.Icon size={12} style={{ flexShrink: 0 }} />}
              <span>{secondary.text}</span>
            </div>
          )}
        </div>
      </div>

      {face.serviceDown && (
        <div style={{ fontSize: 12.5, fontWeight: 700, color: "#d97706", flex: "1 1 200px" }} data-testid="face-enrol-service-down">{t.serviceDownNotice}</div>
      )}
      {!locked && !face.serviceDown && (
        <button
          type="button"
          onClick={startEnrol}
          data-testid="face-enrol-start"
          style={{
            display: "inline-flex", alignItems: "center", gap: 8, padding: "10px 18px", borderRadius: 12, border: "none",
            background: needsReenrol ? "#d97706" : "#2e7cba", color: "#fff", fontWeight: 800, fontSize: 14,
            cursor: "pointer", fontFamily: "inherit", boxShadow: "0 4px 14px rgba(46,124,186,0.3)", flex: "0 0 auto",
          }}
        >
          <ScanFace size={16} />
          {needsReenrol ? t.reenrol : t.enrol}
        </button>
      )}

      <FaceConsentModal
        open={consentOpen}
        isDarkMode={isDarkMode}
        language={language}
        onClose={() => setConsentOpen(false)}
        onAgreed={async () => {
          face.setStatus({ consented: true });
          setConsentOpen(false);
          setEnrolOpen(true);
        }}
      />
      <FaceEnrolModal
        open={enrolOpen}
        isDarkMode={isDarkMode}
        language={language}
        onClose={() => setEnrolOpen(false)}
        onEnrolled={async () => {
          setEnrolOpen(false);
          setNotice({ kind: "success", text: t.enrolSuccess });
          await face.reload();
        }}
      />
    </div>
  );
}
